import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import AvatarImg from "./AvatarImg";
import {
  bildirimOkunduIsaretle,
  bildirimSilService,
  okunmuslariSilService,
  genelBildirimleriSilService,
  sohbetBildirimleriniSilService,
  tumunuOkunduYap,
  dismissNotificationLocal,
} from "../services/bildirimService";
import { useLanguage } from "../i18n/LanguageContext";
import aileasistaniAsset  from "../assets/aileasistaniasset.png";
import aileharitasiAsset  from "../assets/aileharitasiasset.png";
import ailesohbetiAsset   from "../assets/ailesohbetiasset.png";
import alisverisAsset     from "../assets/alisverisasset.png";
import belgelerAsset      from "../assets/belgelerasset.png";
import cocukAsset         from "../assets/cocuktakibiasset.png";
import finansAsset        from "../assets/finansasset.png";
import gorevlerAsset      from "../assets/gorevlerasset.png";
import harcamalarAsset    from "../assets/harcamalarasset.png";
import saglikAsset        from "../assets/saglikasset.png";
import takvimAsset        from "../assets/takvimasset.png";
import { localizeNotificationContent } from "../utils/notificationI18n";
import {
  getPushPermissionStatus,
  requestPushAccess,
  openPushSettings,
  setPushEnablePending,
  PUSH_PERMISSION_EVENT,
} from "../services/pushPermissionService";

// Silinen bildirimleri localStorage'a yaz — polling override'ını engelle
const DISMISSED_KEY = "bp_dismissed_ids";
const DISMISSED_TTL = 180 * 24 * 60 * 60 * 1000;
function dismissId(id) {
  try {
    if (!id) return;
    const raw = localStorage.getItem(DISMISSED_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[String(id)] = Date.now();
    const now = Date.now();
    Object.keys(map).forEach(k => { if (now - map[k] > DISMISSED_TTL) delete map[k]; });
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(map));
  } catch {}
}

function dismissNotif(notif) {
  dismissNotificationLocal(notif);
  dismissId(notif?.id);
  dismissId(notif?._globalNotificationId);
  dismissId(notif?.firebaseId);
}

function purgeNotifCache(ids = []) {
  try {
    const idSet = new Set(ids.filter(Boolean).map(String));
    if (idSet.size === 0) return;
    const raw = localStorage.getItem("fp:notifCache");
    if (!raw) return;
    const items = JSON.parse(raw);
    if (!Array.isArray(items)) return;
    const temiz = items.filter((n) => !idSet.has(String(n.id)) && !idSet.has(String(n._globalNotificationId)) && !idSet.has(String(n.firebaseId)));
    localStorage.setItem("fp:notifCache", JSON.stringify(temiz));
  } catch {}
}

// Bildirim satırı için büyük ikon — asset varsa img, yoksa emoji
const TYPE_ASSET = {
  // Görevler
  shopping:               alisverisAsset,
  shopping_item_added:    alisverisAsset,
  shopping_items_bulk_added: alisverisAsset,
  shopping_item_deleted:  alisverisAsset,
  shopping_item_completed: alisverisAsset,
  // Görevler
  task:                   gorevlerAsset,
  taskDone:               gorevlerAsset,
  task_added:             gorevlerAsset,
  task_completed:         gorevlerAsset,
  task_deleted:           gorevlerAsset,
  // Takvim
  calendar:               takvimAsset,
  birthday:               takvimAsset,
  calendar_event_added:   takvimAsset,
  calendar_event_deleted: takvimAsset,
  calendar_event_updated: takvimAsset,
  // Finans
  finance:                finansAsset,
  finance_added:          finansAsset,
  finance_deleted:        finansAsset,
  sharedExpense:          harcamalarAsset,
  // Sağlık
  health:                 saglikAsset,
  medicine:               saglikAsset,
  cycle:                  saglikAsset,
  // Belgeler
  document:               belgelerAsset,
  document_added:         belgelerAsset,
  document_deleted:       belgelerAsset,
  // Sohbet
  chat:                   ailesohbetiAsset,
  // Harita
  location:               aileharitasiAsset,
  geofence:               aileharitasiAsset,
  yakinlik:               aileharitasiAsset,
  bulusma:                aileharitasiAsset,
  // Asistan
  assistant:              aileasistaniAsset,
  daily_summary:          aileasistaniAsset,
  urgent_alert:           aileasistaniAsset,
  weekly_digest:          aileasistaniAsset,
  smart_insight:          aileasistaniAsset,
  // Çocuk
  children:               cocukAsset,
};

// Filtre chip ve istatistik için fallback emoji (asset yoksa gösterilir)
const TYPE_ICON = {
  shopping: "🛒", shopping_item_added: "🛒", shopping_items_bulk_added: "🛒", shopping_item_deleted: "🛒", shopping_item_completed: "🛒",
  task: "✅", taskDone: "✅", task_added: "✅", task_completed: "✅", task_deleted: "✅",
  calendar: "📅", birthday: "🎂", calendar_event_added: "📅", calendar_event_deleted: "📅", calendar_event_updated: "📅",
  finance: "💰", finance_added: "💰", finance_deleted: "💰", sharedExpense: "💸",
  health: "❤️", medicine: "💊", cycle: "🌸",
  document: "📁", document_added: "📁", document_deleted: "📁",
  chat: "💬",
  location: "📍", geofence: "📍", yakinlik: "📍", bulusma: "🤝",
  assistant: "🤖", daily_summary: "🤖", urgent_alert: "🤖", weekly_digest: "🤖", smart_insight: "🤖",
  children: "👶",
  familyAnnouncement: "👨‍👩‍👧‍👦", system: "📢", systemAnnouncement: "📢", scheduled: "⏰",
  familyStatusUpdated: "📍", familyStatusUpdateRequest: "📍",
  default: "🔔",
};

// Filtre chip'i için küçük ikon — asset varsa küçük img, yoksa emoji
function ChipIcon({ tip }) {
  const asset = TYPE_ASSET[tip];
  if (!asset) return <span>{TYPE_ICON[tip] || TYPE_ICON.default}</span>;
  return <img src={asset} alt={tip} style={{ width: 16, height: 16, borderRadius: 4, objectFit: "cover", verticalAlign: "middle" }} />;
}
// Sub-type'ları ve bildirimType'ları filtre chip'i için ana kategoriye normalize eder
const TYPE_NORM = {
  // Görev
  taskDone:               "task",
  task_added:             "task",
  task_completed:         "task",
  task_deleted:           "task",
  taskReminder:           "task",
  // Takvim
  calendar_event_added:   "calendar",
  calendar_event_deleted: "calendar",
  calendar_event_updated: "calendar",
  calendarReminder:       "calendar",
  eventReminder:          "calendar",
  birthday:               "calendar",
  // Finans
  finance_added:          "finance",
  finance_deleted:        "finance",
  // Belge
  document_added:         "document",
  document_deleted:       "document",
  // Alışveriş
  shopping_item_added:    "shopping",
  shopping_items_bulk_added: "shopping",
  shopping_item_deleted:  "shopping",
  shopping_item_completed:"shopping",
  // Sağlık
  healthReminder:         "health",
  medicineReminder:       "medicine",
  cycleReminder:          "cycle",
  // Asistan
  daily_summary:          "assistant",
  urgent_alert:           "assistant",
  weekly_digest:          "assistant",
  smart_insight:          "assistant",
  // Genel zamanlı → scheduled kalır (manuel/admin hatırlatıcılar)
  scheduledAdmin:         "scheduled",
  userReminder:           "scheduled",
  familyStatusUpdated:    "familyStatusUpdated",
  familyStatusUpdateRequest: "familyStatusUpdateRequest",
  // module field bazlı eşleşme (backend module: "tasks" gibi dönebilir)
  tasks:                  "task",
  gorev:                  "task",
  gorevler:               "task",
  takvim:                 "calendar",
  alisveris:              "shopping",
  finans:                 "finance",
  saglik:                 "health",
  belgeler:               "document",
  sohbet:                 "chat",
  harita:                 "location",
  asistan:                "assistant",
  cocuk:                  "children",
};

// notif.type veya notif.bildirimType'tan normalize tip döndürür
// Backend scheduled bildirimlerinde gerçek kategori bildirimType field'ında taşınır
// baslikKey'den tip çıkarmak için prefix map
const BASLIK_KEY_TIP = {
  "tasks.":            "task",
  "calendar.":         "calendar",
  "health.":           "health",
  "shopping.":         "shopping",
  "finance.":          "finance",
  "expense.":          "finance",
  "document":          "document",
  "notif.auto.task":   "task",
  "notif.auto.calendar": "calendar",
  "notif.auto.medicine": "medicine",
  "notif.auto.document": "document",
};

const tipFromBaslikKey = (baslikKey) => {
  if (!baslikKey) return null;
  for (const [prefix, tip] of Object.entries(BASLIK_KEY_TIP)) {
    if (baslikKey.startsWith(prefix)) return tip;
  }
  return null;
};

const normTip = (type, bildirimType, tip, module_, baslikKey) => {
  // 1. baslikKey'den doğrudan tip çıkar (en güvenilir kaynak)
  const fromKey = tipFromBaslikKey(baslikKey);
  if (fromKey) return fromKey;
  // 2. bildirimType, tip, module sırasıyla
  const candidates = [bildirimType, tip, module_].filter(Boolean);
  for (const c of candidates) {
    if (TYPE_NORM[c]) return TYPE_NORM[c];
    if (c && c !== "scheduled" && c !== "userReminder" && c !== "scheduledAdmin") return c;
  }
  return TYPE_NORM[type] || type || "default";
};

const TYPE_RENK = {
  shopping:               "linear-gradient(135deg,#f59e0b,#d97706)",
  shopping_item_added:    "linear-gradient(135deg,#f59e0b,#d97706)",
  shopping_items_bulk_added: "linear-gradient(135deg,#f59e0b,#d97706)",
  shopping_item_deleted:  "linear-gradient(135deg,#f59e0b,#d97706)",
  shopping_item_completed:"linear-gradient(135deg,#f59e0b,#d97706)",
  task:                   "linear-gradient(135deg,#8b5cf6,#7c3aed)",
  taskDone:               "linear-gradient(135deg,#8b5cf6,#7c3aed)",
  task_added:             "linear-gradient(135deg,#8b5cf6,#7c3aed)",
  task_completed:         "linear-gradient(135deg,#8b5cf6,#7c3aed)",
  task_deleted:           "linear-gradient(135deg,#8b5cf6,#7c3aed)",
  calendar:               "linear-gradient(135deg,#3b82f6,#2563eb)",
  birthday:               "linear-gradient(135deg,#f472b6,#ec4899)",
  calendar_event_added:   "linear-gradient(135deg,#3b82f6,#2563eb)",
  calendar_event_deleted: "linear-gradient(135deg,#3b82f6,#2563eb)",
  calendar_event_updated: "linear-gradient(135deg,#3b82f6,#2563eb)",
  finance:                "linear-gradient(135deg,#10b981,#059669)",
  finance_added:          "linear-gradient(135deg,#10b981,#059669)",
  finance_deleted:        "linear-gradient(135deg,#10b981,#059669)",
  sharedExpense:          "linear-gradient(135deg,#ec4899,#db2777)",
  health:                 "linear-gradient(135deg,#ef4444,#dc2626)",
  medicine:               "linear-gradient(135deg,#22c55e,#16a34a)",
  cycle:                  "linear-gradient(135deg,#f472b6,#ec4899)",
  document:               "linear-gradient(135deg,#14b8a6,#0d9488)",
  document_added:         "linear-gradient(135deg,#14b8a6,#0d9488)",
  document_deleted:       "linear-gradient(135deg,#14b8a6,#0d9488)",
  chat:                   "linear-gradient(135deg,#6366f1,#8b5cf6)",
  location:               "linear-gradient(135deg,#06b6d4,#0284c7)",
  geofence:               "linear-gradient(135deg,#06b6d4,#0284c7)",
  yakinlik:               "linear-gradient(135deg,#06b6d4,#0284c7)",
  bulusma:                "linear-gradient(135deg,#06b6d4,#0284c7)",
  assistant:              "linear-gradient(135deg,#8b5cf6,#6366f1)",
  daily_summary:          "linear-gradient(135deg,#8b5cf6,#6366f1)",
  urgent_alert:           "linear-gradient(135deg,#8b5cf6,#6366f1)",
  weekly_digest:          "linear-gradient(135deg,#8b5cf6,#6366f1)",
  smart_insight:          "linear-gradient(135deg,#8b5cf6,#6366f1)",
  children:               "linear-gradient(135deg,#f59e0b,#f97316)",
  familyAnnouncement:     "linear-gradient(135deg,#6366f1,#8b5cf6)",
  familyStatusUpdated:     "linear-gradient(135deg,#14b8a6,#0891b2)",
  familyStatusUpdateRequest:"linear-gradient(135deg,#14b8a6,#0891b2)",
  system:                 "linear-gradient(135deg,#0ea5e9,#2563eb)",
  systemAnnouncement:     "linear-gradient(135deg,#0ea5e9,#2563eb)",
  scheduled:              "linear-gradient(135deg,#6366f1,#3b82f6)",
  default:                "linear-gradient(135deg,#64748b,#475569)",
};
const TYPE_ETIKET_KEY = {
  shopping: "notif.type.shopping",
  shopping_item_added: "notif.type.shopping",
  shopping_items_bulk_added: "notif.type.shopping",
  shopping_item_deleted: "notif.type.shopping",
  shopping_item_completed: "notif.type.shopping",
  task: "notif.type.task",
  taskDone: "notif.type.task",
  task_added: "notif.type.task",
  task_completed: "notif.type.task",
  task_deleted: "notif.type.task",
  taskReminder: "notif.type.task",
  calendar: "notif.type.calendar",
  birthday: "notif.type.birthday",
  calendar_event_added: "notif.type.calendar",
  calendar_event_deleted: "notif.type.calendar",
  calendar_event_updated: "notif.type.calendar",
  calendarReminder: "notif.type.calendar",
  eventReminder: "notif.type.calendar",
  finance: "notif.type.finance",
  finance_added: "notif.type.finance",
  finance_deleted: "notif.type.finance",
  sharedExpense: "notif.type.expense",
  health: "notif.type.health",
  medicine: "notif.type.medicine",
  cycle: "notif.type.health",
  healthReminder: "notif.type.health",
  medicineReminder: "notif.type.medicine",
  document: "notif.type.document",
  document_added: "notif.type.document",
  document_deleted: "notif.type.document",
  chat: "notif.type.chat",
  location: "notif.type.location",
  geofence: "notif.type.location",
  yakinlik: "notif.type.location",
  bulusma: "notif.type.location",
  assistant: "notif.type.assistant",
  daily_summary: "notif.type.assistant",
  urgent_alert: "notif.type.assistant",
  weekly_digest: "notif.type.assistant",
  smart_insight: "notif.type.assistant",
  children: "notif.type.children",
  familyAnnouncement: "notif.type.family",
  familyStatusUpdated: "notif.type.familyStatus",
  familyStatusUpdateRequest: "notif.type.familyStatus",
  system: "notif.type.system",
  systemAnnouncement: "notif.type.system",
  scheduled: "notif.type.scheduled",
  default: "notif.type.default",
};

const getTipEtiketi = (t, tip) => t(TYPE_ETIKET_KEY[tip] || TYPE_ETIKET_KEY.default);

const getI18nText = (notif, alan, t, lang) => {
  const key = notif?.[`${alan}Key`];
  const vars = notif?.[`${alan}Vars`] || {};
  if (key) return t(key, vars);

  const dict =
    notif?.[`${alan}I18n`] ||
    notif?.[`${alan}_i18n`] ||
    notif?.translations?.[alan] ||
    null;

  if (dict && typeof dict === "object") {
    return dict[lang] || dict.tr || dict.en || notif?.[alan] || "";
  }

  return notif?.[alan] || "";
};

const translateLegacyNotificationText = (notif, t, lang) => {
  // baslikKey varsa direkt rt() ile çevir — pattern listesine gerek yok
  // Bu yol artık tüm yeni bildirimlerde kullanılıyor
  if (notif?.baslikKey || notif?.aciklamaKey) {
    return localizeNotificationContent(notif, lang);
  }

  let baslik   = getI18nText(notif, "baslik",   t, lang);
  let aciklama = getI18nText(notif, "aciklama", t, lang);

  // ── Başlık sabit→key haritası (eski Firestore kayıtları için) ───────────
  const titleKeys = {
    // Alışveriş
    "🛒 Alışveriş Listesi": "notify.shoppingListTitle",
    "🛒 Shopping List": "notify.shoppingListTitle",
    "🛒 Ürün Alındı": "notify.shoppingBoughtTitle",
    "🛒 Product Bought": "notify.shoppingBoughtTitle",
    "🛒 Alışveriş Hatırlatması": "notif.auto.shopping.title",
    "🛒 Shopping Reminder": "notif.auto.shopping.title",
    // Görev
    "✅ Yeni Görev Atandı": "notify.taskAssignedTitle",
    "✅ New Task Assigned": "notify.taskAssignedTitle",
    "✅ Görev Tamamlandı": "notify.taskCompletedTitle",
    "✅ Task Completed": "notify.taskCompletedTitle",
    "🔴 Görev Bugün Son Gün": "notif.auto.task.today.title",
    "🔴 Task is due today": "notif.auto.task.today.title",
    "🔴 Task Due Today": "notif.auto.task.today.title",
    "🟠 Görev Yarın Son Gün": "notif.auto.task.tomorrow.title",
    "🟠 Task is due tomorrow": "notif.auto.task.tomorrow.title",
    "🟠 Task Due Tomorrow": "notif.auto.task.tomorrow.title",
    "🟡 Görev Hatırlatıcı": "notif.auto.task.3days.title",
    "🟡 Task Reminder": "notif.auto.task.3days.title",
    "🔴 Geciken Görev": "notif.auto.task.overdue.title",
    "🔴 Overdue Task": "notif.auto.task.overdue.title",
    // Finans
    "💰 Yeni Gelir": "notify.newIncome",
    "💰 New Income": "notify.newIncome",
    "💰 Yeni Gider": "notify.newExpense",
    "💰 New Expense": "notify.newExpense",
    // Taksit / Abonelik / Fatura
    "🧾 Taksit Ödemesi Bugün": "notif.auto.installment.today.title",
    "🧾 Installment Payment Due Today": "notif.auto.installment.today.title",
    "🧾 Taksit Ödeme Hatırlatması": "notif.auto.installment.days.title",
    "🧾 Installment Payment Reminder": "notif.auto.installment.days.title",
    "🔄 Abonelik Ödemesi Bugün": "notif.auto.subscription.today.title",
    "🔄 Subscription Payment Due Today": "notif.auto.subscription.today.title",
    "🔄 Abonelik Ödeme Hatırlatması": "notif.auto.subscription.days.title",
    "🔄 Subscription Payment Reminder": "notif.auto.subscription.days.title",
    "🔄 Abonelik Yenileme Hatırlatması": "notif.auto.subscription.days.title",
    "📋 Fatura Son Ödeme Günü": "notif.auto.bill.today.title",
    "📋 Bill Due Today": "notif.auto.bill.today.title",
    "📋 Fatura Ödeme Hatırlatması": "notif.auto.bill.days.title",
    "📋 Bill Payment Reminder": "notif.auto.bill.days.title",
    // Belge
    "📁 Yeni Belge Yüklendi": "notify.documentUploadedTitle",
    "📁 New Document Uploaded": "notify.documentUploadedTitle",
    "📁 Belge Hatırlatması": "notif.auto.document.reminder.title",
    "📁 Document Reminder": "notif.auto.document.reminder.title",
    // Harcama
    "💸 Ortak Harcama": "notify.sharedExpenseTitle",
    "💸 Shared Expense": "notify.sharedExpenseTitle",
    // Chat
    "💬 Sohbet": "notify.chatTitleIcon",
    "💬 Chat": "notify.chatTitleIcon",
    "Sohbet": "notify.chatTitle",
    "Chat": "notify.chatTitle",
    // Takvim
    "📅 Yeni Etkinlik": "notify.calendarEventTitle",
    "📅 New Event": "notify.calendarEventTitle",
    "📅 Bugünkü Etkinlik": "notif.auto.calendar.today.title",
    "📅 Today's Event": "notif.auto.calendar.today.title",
    "📅 Yarınki Etkinlik": "notif.auto.calendar.tomorrow.title",
    "📅 Tomorrow's Event": "notif.auto.calendar.tomorrow.title",
    "📅 Bu Haftaki Etkinlik": "notif.auto.calendar.week.title",
    "📅 This Week's Event": "notif.auto.calendar.week.title",
    // Sağlık
    "🏥 Bugünkü Sağlık Randevusu": "notif.auto.appointment.today.title",
    "🏥 Today's Health Appointment": "notif.auto.appointment.today.title",
    "🏥 Yarın Sağlık Randevusu": "notif.auto.appointment.tomorrow.title",
    "🏥 Health Appointment Tomorrow": "notif.auto.appointment.tomorrow.title",
    "💊 İlaç Hatırlatması": "notif.auto.medicine.reminder.title",
    "💊 Medicine Reminder": "notif.auto.medicine.reminder.title",
    "💊 İlaç Bugün Bitiyor": "notif.auto.medicine.ending.title",
    "💊 Medicine Ends Today": "notif.auto.medicine.ending.title",
  };
  if (titleKeys[baslik]) baslik = t(titleKeys[baslik]);

  // ── Açıklama: sadece vars içeren pattern'lar (sabit stringler titleKeys ile halledildi) ─
  const patterns = [
    { re: /^(.+) listeye "(.+)" ekledi$/, key: "notify.shoppingAdded", vars: (m) => ({ name: m[1], item: m[2] }) },
    { re: /^(.+) added "(.+)" to the list$/, key: "notify.shoppingAdded", vars: (m) => ({ name: m[1], item: m[2] }) },
    { re: /^(.+), "(.+)" ürününü alındı olarak işaretledi$/, key: "notify.shoppingBought", vars: (m) => ({ name: m[1], item: m[2] }) },
    { re: /^(.+) marked "(.+)" as bought$/, key: "notify.shoppingBought", vars: (m) => ({ name: m[1], item: m[2] }) },
    { re: /^"(.+)" görevi sana atandı$/, key: "notify.taskAssigned", vars: (m) => ({ task: m[1] }) },
    { re: /^"(.+)" has been assigned to you$/, key: "notify.taskAssigned", vars: (m) => ({ task: m[1] }) },
    { re: /^The task "(.+)" was assigned to you$/, key: "notify.taskAssigned", vars: (m) => ({ task: m[1] }) },
    { re: /^(.+), "(.+)" görevini tamamladı$/, key: "notify.taskCompleted", vars: (m) => ({ name: m[1], task: m[2] }) },
    { re: /^(.+) completed "(.+)"$/, key: "notify.taskCompleted", vars: (m) => ({ name: m[1], task: m[2] }) },
    { re: /^(.+) completed the task "(.+)"$/, key: "notify.taskCompleted", vars: (m) => ({ name: m[1], task: m[2] }) },
    { re: /^"(.+)" görevi bugün tamamlanmalı\.$/, key: "notif.auto.task.today.body", vars: (m) => ({ task: m[1] }) },
    { re: /^"(.+)" must be completed today\.$/, key: "notif.auto.task.today.body", vars: (m) => ({ task: m[1] }) },
    { re: /^"(.+)" görevinin son tarihi yarın\.$/, key: "notif.auto.task.tomorrow.body", vars: (m) => ({ task: m[1] }) },
    { re: /^"(.+)" is due tomorrow\.$/, key: "notif.auto.task.tomorrow.body", vars: (m) => ({ task: m[1] }) },
    { re: /^"(.+)" görevinin son tarihi geçti\.$/, key: "notif.auto.task.overdue.body", vars: (m) => ({ task: m[1] }) },
    { re: /^The due date for "(.+)" has passed\.$/, key: "notif.auto.task.overdue.body", vars: (m) => ({ task: m[1] }) },
    { re: /^"(.+)" taksitinin son ödeme günü bugün\.$/, key: "notif.auto.installment.today.body", vars: (m) => ({ name: m[1] }) },
    { re: /^The installment payment for "(.+)" is due today\.$/, key: "notif.auto.installment.today.body", vars: (m) => ({ name: m[1] }) },
    { re: /^"(.+)" faturasının son ödeme günü bugün\.$/, key: "notif.auto.bill.today.body", vars: (m) => ({ name: m[1] }) },
    { re: /^The bill "(.+)" is due today\.$/, key: "notif.auto.bill.today.body", vars: (m) => ({ name: m[1] }) },
    { re: /^"(.+)" aboneliğinin ödeme günü bugün\.$/, key: "notif.auto.subscription.today.body", vars: (m) => ({ name: m[1] }) },
    { re: /^The subscription payment for "(.+)" is due today\.$/, key: "notif.auto.subscription.today.body", vars: (m) => ({ name: m[1] }) },
    { re: /^"(.+)" ilacını alma zamanı geldi\. Saat: (.+)$/, key: "notif.auto.medicine.reminder.body", vars: (m) => ({ medicine: m[1], time: m[2] }) },
    { re: /^It's time to take "(.+)"\. Time: (.+)$/, key: "notif.auto.medicine.reminder.body", vars: (m) => ({ medicine: m[1], time: m[2] }) },
  ];

  for (const p of patterns) {
    const match = String(aciklama || "").match(p.re);
    if (match) { aciklama = t(p.key, p.vars(match)); break; }
  }

  return localizeNotificationContent({ ...notif, baslik, aciklama }, lang);
};

const AVATAR_RENKLER = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#ec4899,#f43f5e)",
  "linear-gradient(135deg,#10b981,#059669)",
  "linear-gradient(135deg,#f59e0b,#d97706)",
  "linear-gradient(135deg,#06b6d4,#0891b2)",
];

function zamanStr(ts, detayli = false, t, lang = "tr") {
  if (!ts) return "";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const locale = lang === "en" ? "en-US" : "tr-TR";
    if (detayli) {
      return d.toLocaleString(locale, {
        day: "numeric", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    }
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60)     return t("notification.ago.now");
    if (diff < 3600)   return `${Math.floor(diff / 60)} ${t("notification.ago.minutes")}`;
    if (diff < 86400)  return `${Math.floor(diff / 3600)} ${t("notification.ago.hours")}`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} ${t("notification.ago.days")}`;
    return d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  } catch { return ""; }
}


function YildizIkon({ aktif }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24"
      fill={aktif ? "#f59e0b" : "none"}
      stroke={aktif ? "#f59e0b" : "currentColor"}
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function EylemButon({ type, onClick, t }) {
  const etiketler = {
    shopping: "notification.action.shopping",
    task: "notification.action.task",
    calendar: "notification.action.calendar",
    finance: "notification.action.finance",
    sharedExpense: "notification.action.sharedExpense",
    health: "notification.action.health",
    birthday: "notification.action.calendar",
    chat: "notification.action.chat",
    medicine: "notification.action.health",
    document: "notification.action.document",
    familyAnnouncement: "notification.action.open",
    system: "notification.action.announcement",
    systemAnnouncement: "notification.action.announcement",
    scheduled: "notification.action.open",
  };
  const etiket = etiketler[type] ? t(etiketler[type]) : null;
  if (!etiket) return null;
  return (
    <button className="bildirimEylemBtn" onClick={onClick} title={etiket}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
      {etiket}
    </button>
  );
}


/* ── Tek bildirim satırı ── */
function BildirimSatir({ notif, familyId, userUid, onSilLocal, onOkuLocal, onBildirimTikla, uyeler = [], onYildizToggle, t, lang }) {
  const [swipeX, setSwipeX]   = useState(0);
  const [siliyor, setSiliyor] = useState(false);
  const [tooltip, setTooltip] = useState(false);
  const [genisletildi, setGenisletildi] = useState(false);
  const startXRef             = useRef(0);
  const isDraggingRef         = useRef(false);
  const swipeXRef             = useRef(0);

  const handleTouchStart = (e) => {
    startXRef.current     = e.touches[0].clientX;
    isDraggingRef.current = true;
  };
  const handleTouchMove = (e) => {
    if (!isDraggingRef.current) return;
    const dx = e.touches[0].clientX - startXRef.current;
    if (dx < -10) {
      swipeXRef.current = Math.max(dx, -120);
      setSwipeX(swipeXRef.current);
    } else if (dx > 10) {
      swipeXRef.current = Math.min(dx, 80);
      setSwipeX(swipeXRef.current);
    }
  };
  const handleTouchEnd = () => {
    isDraggingRef.current = false;
    if (swipeXRef.current < -80) {
      silNotif();
    } else if (swipeXRef.current > 50) {
      onYildizToggle && onYildizToggle(notif.id);
      setSwipeX(0);
      swipeXRef.current = 0;
    } else {
      setSwipeX(0);
      swipeXRef.current = 0;
    }
  };

  const silNotif = () => {
    setSiliyor(true);
    setTimeout(() => {
      onSilLocal(notif.id);
      if (!String(notif.id).startsWith("local-")) {
        bildirimSilService(familyId, userUid, notif.id).catch(() => {});
      }
    }, 280);
  };

  const handleOku = () => {
    // İlk dokunuş: bildirimi okundu yap ve metni genişlet.
    // İkinci dokunuş: mevcut yönlendirme davranışını çalıştır.
    if (!genisletildi) {
      setGenisletildi(true);
      if (!notif.okundu) {
        onOkuLocal(notif.id, true);
        if (!String(notif.id).startsWith("local-")) {
          bildirimOkunduIsaretle(familyId, userUid, notif.id).catch(() => {});
        }
      }
      return;
    }
    if (onBildirimTikla) onBildirimTikla(notif);
  };

  const handleSil = (e) => { e.stopPropagation(); silNotif(); };

  const handleEylem = (e) => { e.stopPropagation(); if (onBildirimTikla) onBildirimTikla(notif); };

  const handleOkuToggle = (e) => {
    e.stopPropagation();
    const yeniDurum = !notif.okundu;
    onOkuLocal(notif.id, yeniDurum);
    if (yeniDurum && !String(notif.id).startsWith("local-")) {
      bildirimOkunduIsaretle(familyId, userUid, notif.id).catch(() => {});
    }
  };

  let avatarUye = null;
  if (notif.type === "chat" && notif.gonderenUid && uyeler.length > 0) {
    avatarUye = uyeler.find(u => u.uid === notif.gonderenUid) || null;
  }
  const avatarRenk = avatarUye?.calendarColor ||
    (avatarUye ? AVATAR_RENKLER[uyeler.findIndex(u => u.uid === notif.gonderenUid) % AVATAR_RENKLER.length] : null);
  const ikonBg = avatarRenk || (TYPE_RENK[normTip(notif.type, notif.bildirimType, notif.tip, notif.module, notif.baslikKey)] || TYPE_RENK[notif.type] || TYPE_RENK.default);
  const swipeLeft  = swipeX < -20;
  const swipeRight = swipeX > 20;

  return (
    <div className={`bildirimSatirWrap${siliyor ? " bildirimSatirSiliyor" : ""}${notif.yildizli ? " bildirimSatirYildizliWrap" : ""}`}>
      <div className={`bildirimSwipeArkaLeft${swipeLeft ? " gorunur" : ""}`}>
        <span>🗑</span><span className="bildirimSwipeArkaYazi">{t("common.delete")}</span>
      </div>
      <div className={`bildirimSwipeArkaRight${swipeRight ? " gorunur" : ""}`}>
        <span className="bildirimSwipeArkaYazi">{notif.yildizli ? t("notification.star.remove") : t("notification.star.add")}</span>
        <span>⭐</span>
      </div>

      <div
        className={`bildirimSatir${notif.okundu ? " okundu" : " okunmamis"}${notif.yildizli ? " bildirimSatirYildizli" : ""}${genisletildi ? " genisletildi" : ""}`}
        style={{ transform: `translateX(${swipeX}px)`, transition: isDraggingRef.current ? "none" : "transform 0.25s ease" }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleOku}
      >
        {avatarUye ? (
          <AvatarImg
            avatar={avatarUye.avatar || null}
            renk={avatarRenk}
            initials={(avatarUye.ad?.[0] || "?").toUpperCase()}
            size={40}
            glow={false}
            radius="12px"
            className="bildirimSatirIkon"
          />
        ) : (TYPE_ASSET[normTip(notif.type, notif.bildirimType, notif.tip, notif.module, notif.baslikKey)] || TYPE_ASSET[notif.type]) ? (
          <div className="bildirimSatirIkon bildirimSatirIkonAsset">
            <img src={TYPE_ASSET[normTip(notif.type, notif.bildirimType, notif.tip, notif.module, notif.baslikKey)] || TYPE_ASSET[notif.type]} alt={notif.type} style={{ width: 40, height: 40, borderRadius: 10, objectFit: "cover" }} />
          </div>
        ) : (
          <div className="bildirimSatirIkon" style={{ background: ikonBg }}>
            {TYPE_ICON[normTip(notif.type, notif.bildirimType, notif.tip, notif.module, notif.baslikKey)] || TYPE_ICON.default}
          </div>
        )}

        <div className="bildirimSatirIcerik">
          <div className="bildirimSatirBaslikSatir">
            <div className="bildirimSatirBaslik">{notif.baslik}</div>
            {notif.yildizli && <span className="bildirimYildizIkon"><YildizIkon aktif /></span>}
          </div>
          <div className="bildirimSatirAciklama">{notif.aciklama}</div>
          <div className="bildirimSatirAlt">
            <span
              className="bildirimSatirZaman"
              onMouseEnter={() => setTooltip(true)}
              onMouseLeave={() => setTooltip(false)}
            >
              {zamanStr(notif.createdAt, false, t, lang)}
              {tooltip && <span className="bildirimZamanTooltip">{zamanStr(notif.createdAt, true, t, lang)}</span>}
            </span>
            {notif.familyName && notif.familyId !== familyId && (
              <span className="bildirimAileChip" title={notif.familyName}>
                🏠 {notif.familyName}
              </span>
            )}
            {!notif.okundu && <EylemButon type={notif.type} onClick={handleEylem} t={t} />}
          </div>
        </div>

        <div className="bildirimSatirSagKisim">
          <button
            className={`bildirimOkuToggle${notif.okundu ? " okundu" : " okunmamis"}`}
            onClick={handleOkuToggle}
            title={notif.okundu ? t("notification.markUnread") : t("notification.markRead")}
          >
            {notif.okundu
              ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="9"/></svg>
              : <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="9"/></svg>
            }
          </button>
          <button
            className={`bildirimYildizBtn${notif.yildizli ? " aktif" : ""}`}
            onClick={(e) => { e.stopPropagation(); onYildizToggle && onYildizToggle(notif.id); }}
            title={notif.yildizli ? t("notification.star.remove") : t("notification.star.add")}
          >
            <YildizIkon aktif={!!notif.yildizli} />
          </button>
          <button className="bildirimSilBtn" onClick={handleSil} aria-label={t("common.delete")}>×</button>
        </div>
      </div>
    </div>
  );
}

function TipChip({ tip, sayi, aktif, onClick, sadece_ikon = false, t }) {
  return (
    <button
      className={`bildirimTipChip${aktif ? " aktif" : ""}${sadece_ikon ? " sadece-ikon" : ""}`}
      style={aktif && tip !== "hepsi" ? { background: TYPE_RENK[tip] || TYPE_RENK.default, borderColor: "transparent" } : undefined}
      onClick={onClick}
      title={tip === "hepsi" ? t("common.all") : getTipEtiketi(t, tip)}
    >
      {tip !== "hepsi" && <ChipIcon tip={tip} />}
      <span className="bildirimTipChipLabel">
        {tip === "hepsi" ? t("common.all") : getTipEtiketi(t, tip)}
      </span>
      {sayi > 0 && <span className="bildirimTipChipSayi">{sayi}</span>}
    </button>
  );
}

function IstatistikPanel({ bildirimler, chatBildirimler, t }) {
  const tumBildirimler = [...bildirimler, ...chatBildirimler];
  const toplam     = tumBildirimler.length;
  const okunmamis  = tumBildirimler.filter(n => !n.okundu).length;
  const okunmus    = toplam - okunmamis;
  const oran       = toplam > 0 ? Math.round((okunmus / toplam) * 100) : 100;

  const turDagilim = useMemo(() => {
    const s = {};
    tumBildirimler.forEach(n => { const t = normTip(n.type, n.bildirimType, n.tip, n.module, n.baslikKey); s[t] = (s[t] || 0) + 1; });
    return Object.entries(s).sort((a, b) => b[1] - a[1]).slice(0, 4);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bildirimler, chatBildirimler]);

  return (
    <div className="bildirimIstatPanel">
      <div className="bildirimIstatSatir">
        <div className="bildirimIstatKart">
          <div className="bildirimIstatSayi">{toplam}</div>
          <div className="bildirimIstatEtiket">{t("common.total")}</div>
        </div>
        <div className="bildirimIstatKart okunmamis">
          <div className="bildirimIstatSayi">{okunmamis}</div>
          <div className="bildirimIstatEtiket">{t("notification.unread")}</div>
        </div>
        <div className="bildirimIstatKart okunmus">
          <div className="bildirimIstatSayi">{okunmus}</div>
          <div className="bildirimIstatEtiket">{t("notification.read")}</div>
        </div>
        <div className="bildirimIstatKart oran">
          <div className="bildirimIstatSayi">%{oran}</div>
          <div className="bildirimIstatEtiket">{t("notification.readRate")}</div>
        </div>
      </div>
      {turDagilim.length > 0 && (
        <div className="bildirimIstatTurler">
          {turDagilim.map(([tip, sayi]) => (
            <div key={tip} className="bildirimIstatTur">
              <div className="bildirimIstatTurIkon" style={{ background: TYPE_RENK[tip] || TYPE_RENK.default }}>
                <ChipIcon tip={tip} />
              </div>
              <div className="bildirimIstatTurAd">{getTipEtiketi(t, tip)}</div>
              <div className="bildirimIstatTurBar">
                <div className="bildirimIstatTurBarDolu"
                  style={{ width: `${Math.min((sayi / toplam) * 100, 100)}%`, background: TYPE_RENK[tip] || TYPE_RENK.default }} />
              </div>
              <div className="bildirimIstatTurSayi">{sayi}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ── Bildirim popup: iPhone tarzı üstten/alttan sürükle-kapat ── */
function useBildirimSwipeClose(onClose) {
  const popupRef = useRef(null);
  const overlayRef = useRef(null);
  const startY = useRef(0);
  const lastY = useRef(0);
  const directionRef = useRef("down");
  const startTime = useRef(0);
  const active = useRef(false);
  const dragging = useRef(false);
  const pointerId = useRef(null);
  const closeTimer = useRef(null);

  const resetInlineStyles = useCallback(() => {
    const popup = popupRef.current;
    const overlay = overlayRef.current;
    if (!popup || !overlay) return;
    popup.style.animation = "";
    popup.style.transition = "";
    popup.style.transform = "";
    popup.style.opacity = "";
    overlay.style.transition = "";
    overlay.style.opacity = "";
  }, []);

  const finishClose = useCallback((direction = "down") => {
    const popup = popupRef.current;
    const overlay = overlayRef.current;
    if (!popup || !overlay) {
      onClose?.();
      return;
    }

    const hedefY = direction === "up" ? "-110vh" : "110vh";
    popup.style.transition = "transform 0.26s cubic-bezier(0.32,0,0.67,0), opacity 0.22s ease";
    popup.style.transform = `translate3d(0, ${hedefY}, 0)`;
    popup.style.opacity = "0";
    overlay.style.transition = "opacity 0.24s ease";
    overlay.style.opacity = "0";

    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      onClose?.();
      window.requestAnimationFrame(resetInlineStyles);
    }, 240);
  }, [onClose, resetInlineStyles]);

  useEffect(() => {
    const popup = popupRef.current;
    const overlay = overlayRef.current;
    if (!popup || !overlay) return;

    const getY = (event) => event.touches?.[0]?.clientY ?? event.changedTouches?.[0]?.clientY ?? event.clientY;

    const beginDrag = (event) => {
      const topHandle = event.target?.closest?.(".bildirimSwipeHandleTop");
      const bottomHandle = event.target?.closest?.(".bildirimSwipeHandleBottom");
      if (!topHandle && !bottomHandle) return;

      active.current = true;
      dragging.current = false;
      pointerId.current = event.pointerId ?? null;
      directionRef.current = bottomHandle ? "up" : "down";
      startY.current = getY(event);
      lastY.current = startY.current;
      startTime.current = Date.now();

      if (event.pointerId != null && popup.setPointerCapture) {
        try { popup.setPointerCapture(event.pointerId); } catch {}
      }
    };

    const moveDrag = (event) => {
      if (!active.current) return;
      if (pointerId.current != null && event.pointerId != null && event.pointerId !== pointerId.current) return;

      const currentY = getY(event);
      const signedDelta = currentY - startY.current;
      const delta = directionRef.current === "up" ? Math.max(0, -signedDelta) : Math.max(0, signedDelta);
      lastY.current = currentY;

      if (!dragging.current) {
        if (delta < 14) return;
        dragging.current = true;
        popup.style.animation = "none";
        popup.style.transition = "none";
        popup.style.willChange = "transform, opacity";
        overlay.style.transition = "none";
        overlay.style.willChange = "opacity";
      }

      event.preventDefault?.();
      const progress = Math.min(delta / (window.innerHeight * 0.50), 1);
      const translateY = directionRef.current === "up" ? -delta : delta;
      const scale = 1 - progress * 0.025;
      popup.style.transform = `translate3d(0, ${translateY}px, 0) scale(${scale})`;
      popup.style.opacity = String(Math.max(0.34, 1 - progress * 0.66));
      overlay.style.opacity = String(Math.max(0, 1 - progress * 1.05));
    };

    const endDrag = (event) => {
      if (!active.current) return;
      if (pointerId.current != null && event.pointerId != null && event.pointerId !== pointerId.current) return;

      active.current = false;
      if (event.pointerId != null && popup.releasePointerCapture) {
        try { popup.releasePointerCapture(event.pointerId); } catch {}
      }

      if (!dragging.current) return;
      dragging.current = false;

      const signedDelta = lastY.current - startY.current;
      const delta = directionRef.current === "up" ? Math.max(0, -signedDelta) : Math.max(0, signedDelta);
      const elapsed = Math.max(Date.now() - startTime.current, 1);
      const velocity = delta / elapsed;
      const closeThreshold = Math.min(window.innerHeight * 0.30, 240);
      const shouldClose = delta > closeThreshold || (delta > 95 && velocity > 0.75);

      if (shouldClose) {
        finishClose(directionRef.current);
        return;
      }

      popup.style.transition = "transform 0.42s cubic-bezier(0.34,1.42,0.64,1), opacity 0.28s ease";
      popup.style.transform = "translate3d(0,0,0) scale(1)";
      popup.style.opacity = "1";
      overlay.style.transition = "opacity 0.28s ease";
      overlay.style.opacity = "1";
      window.setTimeout(() => { if (!active.current) resetInlineStyles(); }, 430);
    };

    const cancelDrag = () => {
      if (!active.current) return;
      active.current = false;
      dragging.current = false;
      popup.style.transition = "transform 0.30s ease, opacity 0.24s ease";
      popup.style.transform = "translate3d(0,0,0) scale(1)";
      popup.style.opacity = "1";
      overlay.style.transition = "opacity 0.24s ease";
      overlay.style.opacity = "1";
      window.setTimeout(resetInlineStyles, 310);
    };

    popup.addEventListener("pointerdown", beginDrag, { passive: true, capture: true });
    popup.addEventListener("pointermove", moveDrag, { passive: false, capture: true });
    popup.addEventListener("pointerup", endDrag, { passive: true, capture: true });
    popup.addEventListener("pointercancel", cancelDrag, { passive: true, capture: true });

    return () => {
      window.clearTimeout(closeTimer.current);
      popup.removeEventListener("pointerdown", beginDrag, { capture: true });
      popup.removeEventListener("pointermove", moveDrag, { capture: true });
      popup.removeEventListener("pointerup", endDrag, { capture: true });
      popup.removeEventListener("pointercancel", cancelDrag, { capture: true });
    };
  }, [finishClose, resetInlineStyles]);

  return { popupRef, overlayRef };
}

/* ── Ana popup ── */
export default function BildirimPopup({
  acik, kapat,
  bildirimler = [],
  chatBildirimler = [],
  familyId, userUid, user,
  onBildirimlerGuncelle,
  onChatBildirimlerGuncelle,
  uyeler = [],
  onBildirimTikla,
}) {
  const { t, lang } = useLanguage();
  const [sekme, setSekme]         = useState("genel");
  const [filtre, setFiltre]       = useState("hepsi");
  const [tipFiltre, setTipFiltre] = useState("hepsi");
  const [arama, setArama]         = useState("");
  const [istatAcik, setIstatAcik] = useState(false);
  const [pushPermission, setPushPermission] = useState("checking");
  const [yildizliIds, setYildizliIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("bp_yildizli") || "[]")); }
    catch { return new Set(); }
  });
  const { popupRef, overlayRef } = useBildirimSwipeClose(kapat);
  const aramaRef   = useRef(null);

  useEffect(() => {
    if (!acik) return;
    const fn = (e) => { if (overlayRef.current === e.target) kapat(); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [acik, kapat, overlayRef]);


  useEffect(() => {
    if (!acik) return;
    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    document.body.classList.add("bildirim-modal-open");
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.classList.remove("bildirim-modal-open");
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
    };
  }, [acik]);

  useEffect(() => {
    if (!acik) return;
    const fn = (e) => { if (e.key === "Escape") kapat(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [acik, kapat]);

  useEffect(() => {
    if (!acik) return;
    let active = true;
    const refresh = async () => {
      const status = await getPushPermissionStatus();
      if (active) setPushPermission(status);
    };
    refresh().catch(() => setPushPermission("unsupported"));
    window.addEventListener(PUSH_PERMISSION_EVENT, refresh);
    return () => {
      active = false;
      window.removeEventListener(PUSH_PERMISSION_EVENT, refresh);
    };
  }, [acik]);

  const bildirimIzniAc = async () => {
    if (pushPermission === "granted" || pushPermission === "checking") return;
    if (pushPermission === "denied") {
      setPushEnablePending(userUid, true);
      await openPushSettings();
      return;
    }
    const status = await requestPushAccess({ user: user || { uid: userUid }, familyId, lang });
    setPushPermission(status);
  };

  /* ── Tüm hook'lar early return'den ÖNCE ── */
  const aktifListe = useMemo(() => {
    const kaynak = sekme === "chat" ? chatBildirimler : bildirimler;
    return kaynak.map(n => ({ ...n, ...translateLegacyNotificationText(n, t, lang), yildizli: yildizliIds.has(n.id) }));
  }, [sekme, bildirimler, chatBildirimler, yildizliIds, t, lang]);

  const tipSayilari = useMemo(() => {
    const s = { hepsi: aktifListe.length };
    aktifListe.forEach(n => { const t = normTip(n.type, n.bildirimType, n.tip, n.module, n.baslikKey); s[t] = (s[t] || 0) + 1; });
    return s;
  }, [aktifListe]);

  if (!acik) return null;

  const siraliListe = [...aktifListe].sort((a, b) => {
    if (a.yildizli && !b.yildizli) return -1;
    if (!a.yildizli && b.yildizli)  return 1;
    return 0;
  });

  const okunmamisFiltreli = filtre === "okunmamis" ? siraliListe.filter(n => !n.okundu)
    : filtre === "yildizli" ? siraliListe.filter(n => n.yildizli)
    : siraliListe;

  const tipFiltreli = tipFiltre === "hepsi"
    ? okunmamisFiltreli
    : okunmamisFiltreli.filter(n => normTip(n.type, n.bildirimType, n.tip, n.module, n.baslikKey) === tipFiltre);

  const aramaFiltreli = arama.trim()
    ? tipFiltreli.filter(n =>
        (n.baslik || "").toLowerCase().includes(arama.toLowerCase()) ||
        (n.aciklama || "").toLowerCase().includes(arama.toLowerCase()))
    : tipFiltreli;

  const okunmamis     = bildirimler.filter(n => !n.okundu).length;
  const chatOkunmamis = chatBildirimler.filter(n => !n.okundu).length;

  const mevcutTipler = Object.keys(tipSayilari).filter(t => t !== "hepsi" && tipSayilari[t] > 0);

  const handleYildizToggle = (id) => {
    setYildizliIds(prev => {
      const yeni = new Set(prev);
      if (yeni.has(id)) yeni.delete(id);
      else yeni.add(id);
      try { localStorage.setItem("bp_yildizli", JSON.stringify([...yeni])); } catch {}
      return yeni;
    });
  };

  const handleSilLocal = (id) => {
    const mevcut = (sekme === "chat" ? chatBildirimler : bildirimler).find(n => n.id === id || n._globalNotificationId === id);
    if (mevcut) {
      dismissNotif(mevcut);
      purgeNotifCache([mevcut.id, mevcut._globalNotificationId, mevcut.firebaseId]);
    }
    if (sekme === "chat") onChatBildirimlerGuncelle(prev => prev.filter(n => n.id !== id && n._globalNotificationId !== id));
    else onBildirimlerGuncelle(prev => prev.filter(n => n.id !== id && n._globalNotificationId !== id));
    setYildizliIds(prev => { const s = new Set(prev); s.delete(id); return s; });
  };

  const handleOkuLocal = (id, durum = true) => {
    const fn = (prev) => prev.map(n => n.id === id ? { ...n, okundu: durum } : n);
    if (sekme === "chat") onChatBildirimlerGuncelle(fn);
    else onBildirimlerGuncelle(fn);
  };

  const scopeFamilyIds = (list = []) => {
    const ids = [...new Set((list || []).map(n => n.familyId || n._familyScopeId).filter(Boolean))];
    if (familyId && !ids.includes(familyId)) ids.push(familyId);
    return ids;
  };

  const handleTumunuOku = () => {
    const fn = (prev) => prev.map(n => ({ ...n, okundu: true }));
    onBildirimlerGuncelle(fn);
    onChatBildirimlerGuncelle(fn);
    if (userUid) {
      const hedefFamilyIds = [...new Set([
        ...scopeFamilyIds(bildirimler),
        ...scopeFamilyIds(chatBildirimler),
      ])];
      hedefFamilyIds.forEach(fid => tumunuOkunduYap(fid, userUid).catch(() => {}));
    }
  };

  const handleOkunmuslariSil = () => {
    const hedefler = sekme === "chat" ? chatBildirimler : bildirimler;
    const okunmuslar = hedefler.filter(n => n.okundu);
    okunmuslar.forEach(dismissNotif);
    purgeNotifCache(okunmuslar.flatMap(n => [n.id, n._globalNotificationId, n.firebaseId]));
    const fn = (prev) => prev.filter(n => !n.okundu);
    if (sekme === "chat") onChatBildirimlerGuncelle(fn);
    else onBildirimlerGuncelle(fn);
    if (userUid) {
      const familyIds = [...new Set(okunmuslar.map(n => n.familyId || n._familyScopeId).filter(Boolean))];
      familyIds.forEach(fid => okunmuslariSilService(fid, userUid).catch(() => {}));
      if (familyId && !familyIds.includes(familyId)) {
        okunmuslariSilService(familyId, userUid).catch(() => {});
      }
    }
  };

  const handleTumunuSil = () => {
    if (sekme === "chat") {
      // Tüm chat bildirim ID'lerini dismiss et (localStorage)
      chatBildirimler.forEach(dismissNotif);
      purgeNotifCache(chatBildirimler.flatMap(n => [n.id, n._globalNotificationId, n.firebaseId]));
      onChatBildirimlerGuncelle(() => []);
      // Her ailenin bildirimlerini kendi familyId'siyle sil
      if (userUid) {
        const familyIds = [...new Set(chatBildirimler.map(n => n.familyId || n._familyScopeId).filter(Boolean))];
        familyIds.forEach(fid => {
          sohbetBildirimleriniSilService(fid, userUid, null).catch(() => {});
        });
        // Fallback: aktif familyId ile de sil
        if (familyId && !familyIds.includes(familyId)) {
          sohbetBildirimleriniSilService(familyId, userUid, null).catch(() => {});
        }
      }
    } else {
      // Tüm genel bildirim ID'lerini dismiss et (localStorage)
      bildirimler.forEach(dismissNotif);
      purgeNotifCache(bildirimler.flatMap(n => [n.id, n._globalNotificationId, n.firebaseId]));
      onBildirimlerGuncelle(() => []);
      // Her ailenin bildirimlerini kendi familyId'siyle sil
      if (userUid) {
        const familyIds = [...new Set(bildirimler.map(n => n.familyId || n._familyScopeId).filter(Boolean))];
        familyIds.forEach(fid => {
          genelBildirimleriSilService(fid, userUid).catch(() => {});
        });
        if (familyId && !familyIds.includes(familyId)) {
          genelBildirimleriSilService(familyId, userUid).catch(() => {});
        }
      }
    }
  };

  return (
    <div className="bildirimPopupOverlay" ref={overlayRef}>
      <div className="bildirimPopup" ref={popupRef}>
        <div className="bildirimSwipeHandleTop" aria-hidden="true"><span /></div>

        {/* Başlık */}
        <div className="bildirimPopupHeader">
          <div className="bildirimPopupBaslik">
            <span>🔔</span>
            <span>{t("notification.title")}</span>
            {(okunmamis + chatOkunmamis) > 0 && (
              <span className="bildirimPopupSayi">{okunmamis + chatOkunmamis}</span>
            )}
          </div>
          <div className="bildirimHeaderSag">
            {pushPermission !== "unsupported" && pushPermission !== "checking" && (
              <button
                className={`bildirimIzinDurumBtn ${pushPermission === "granted" ? "aktif" : "pasif"}`}
                onClick={bildirimIzniAc}
                title={pushPermission === "granted" ? t("notification.permissionOn") : t("notification.permissionAction")}
                aria-label={pushPermission === "granted" ? t("notification.permissionOn") : t("notification.permissionOff")}
              >
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                  <path d="M10 21h4" />
                  {pushPermission !== "granted" && <path d="M4 4l16 16" />}
                </svg>
                <span className="bildirimIzinNokta" aria-hidden="true" />
              </button>
            )}
            <button
              className={`bildirimIstatBtn${istatAcik ? " aktif" : ""}`}
              onClick={() => setIstatAcik(v => !v)}
              title={t("notification.stats")}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6"  y1="20" x2="6"  y2="14"/>
              </svg>
            </button>
            <button className="bildirimPopupKapat" onClick={kapat}>×</button>
          </div>
        </div>

        {/* İstatistik paneli — artık popup dışında değil, liste içinde */}

        {/* Sekmeler */}
        <div className="bildirimSekmeler">
          <button
            className={`bildirimSekme${sekme === "genel" ? " aktif" : ""}`}
            onClick={() => { setSekme("genel"); setTipFiltre("hepsi"); setArama(""); }}
          >
            📋 {t("notification.tab.general")} {okunmamis > 0 && <span className="bildirimSekmeSayi">{okunmamis}</span>}
          </button>
          <button
            className={`bildirimSekme${sekme === "chat" ? " aktif" : ""}`}
            onClick={() => { setSekme("chat"); setTipFiltre("hepsi"); setArama(""); }}
          >
            💬 {t("notification.tab.chat")} {chatOkunmamis > 0 && <span className="bildirimSekmeSayi">{chatOkunmamis}</span>}
          </button>
        </div>

        {/* Arama */}
        <div className="bildirimAramaWrap">
          <svg className="bildirimAramaIkon" width="14" height="14" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref={aramaRef}
            className="bildirimAramaInput"
            type="text"
            placeholder={t("notification.searchPlaceholder")}
            value={arama}
            onChange={e => setArama(e.target.value)}
          />
          {arama && (
            <button className="bildirimAramaSifirla" onClick={() => { setArama(""); aramaRef.current?.focus(); }}>×</button>
          )}
        </div>

        {/* Filtre + Aksiyonlar — sabit bar */}
        <div className="bildirimAksiyonBar">
          <div className="bildirimFiltreGrup">
            <button className={`bildirimFiltreBtn${filtre === "hepsi" ? " aktif" : ""}`} onClick={() => setFiltre("hepsi")}>{t("common.all")}</button>
            <button className={`bildirimFiltreBtn${filtre === "okunmamis" ? " aktif" : ""}`} onClick={() => setFiltre("okunmamis")}>
              {t("notification.unread")}
              {(okunmamis + chatOkunmamis) > 0 && <span className="bildirimFiltreSekmeSayi">{okunmamis + chatOkunmamis}</span>}
            </button>
            <button className={`bildirimFiltreBtn${filtre === "yildizli" ? " aktif yildizAktif" : ""}`} onClick={() => setFiltre("yildizli")}>⭐</button>
          </div>
          <div className="bildirimAksiyonGrup">
            <button className="bildirimMiniBtn" onClick={handleTumunuOku} title={t("notification.markAllRead")}>✓ {t("notification.markAllReadShort")}</button>
            <button className="bildirimMiniBtn danger" onClick={handleOkunmuslariSil} title={t("notification.deleteRead")}>🗑</button>
          </div>
        </div>

        {/* Liste — istatistik + chip'ler + bildirimler birlikte kayar */}
        <div className="bildirimListe">

          {/* İstatistik paneli — scroll ile kayar */}
          {istatAcik && (
            <IstatistikPanel bildirimler={bildirimler} chatBildirimler={chatBildirimler} t={t} />
          )}

          {/* Tip chip'leri — scroll ile kayar */}
          {mevcutTipler.length > 1 && (
            <div className={`bildirimTipChipler${mevcutTipler.length >= 5 ? " kompakt" : ""}`}>
              <TipChip
                tip="hepsi"
                sayi={tipSayilari["hepsi"]}
                aktif={tipFiltre === "hepsi"}
                onClick={() => setTipFiltre("hepsi")}
                sadece_ikon={false}
                t={t}
              />
              {mevcutTipler.map(tip => (
                <TipChip
                  key={tip}
                  tip={tip}
                  sayi={tipSayilari[tip]}
                  aktif={tipFiltre === tip}
                  onClick={() => setTipFiltre(tip)}
                  sadece_ikon={mevcutTipler.length >= 7}
                  t={t}
                />
              ))}
            </div>
          )}

          {aramaFiltreli.length === 0 ? (
            <div className="bildirimBos">
              <div className="bildirimBosIkon">
                {arama ? "🔍" : filtre === "yildizli" ? "⭐" : "🔕"}
              </div>
              <div className="bildirimBosBaslik">
                {arama ? t("notification.empty.search")
                  : filtre === "yildizli" ? t("notification.empty.starred")
                  : filtre === "okunmamis" ? t("notification.empty.unread")
                  : t("notification.empty")}
              </div>
              {arama && (
                <div className="bildirimBosAlt">{t("notification.empty.searchFor", { query: arama })}</div>
              )}
            </div>
          ) : (
            aramaFiltreli.map(notif => (
              <BildirimSatir
                key={notif.id}
                notif={notif}
                familyId={familyId}
                userUid={userUid}
                onSilLocal={handleSilLocal}
                onOkuLocal={handleOkuLocal}
                onBildirimTikla={onBildirimTikla}
                uyeler={uyeler}
                onYildizToggle={handleYildizToggle}
                t={t}
                lang={lang}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {aramaFiltreli.length > 0 && (
          <div className="bildirimPopupFooter">
            <span className="bildirimPopupFooterSayi">
              {aramaFiltreli.length} {t("notification.count")}{arama ? ` · "${arama}"` : ""}
            </span>
            <button className="bildirimMiniBtn danger" onClick={handleTumunuSil}>{t("notification.clearAll")}</button>
          </div>
        )}
        <div className="bildirimSwipeHandleBottom" aria-hidden="true"><span /></div>
      </div>
    </div>
  );
}
